/* eslint-disable react/prop-types */
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import UiKitButton from 'components/UiKit/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import { Context } from 'components/Store';
import FormattedNumber from 'components/FormattedNumber';
import { paths } from 'routes/paths';
import YieldIcon from 'assets/yield_icon.svg';
import DeleteIcon from 'assets/deleteIcon.svg';
import getLoadUnitAbbreviation from 'screens/YieldV2/LoadDetails/helpers/getLoadUnitAbbreviation';
import EditIcon from 'assets/edit_outlined.svg';
import LoadDetailsDrawer from '../LoadDetailsDrawer';
import PropertyAsSourceDetails from '../SourceDetails/PropertyAsSourceDetails';
import LoadDetailsInfoTabs from './components/LoadDetailsInfoTabs';
import StorageAsSourceDetails from '../SourceDetails/StorageAsSourceDetails';
import {
  ICONS_BY_LOAD_DIRECTION,
  LOAD_TYPES,
  TLoad,
  treeDataPropType
} from '../../../Loads/constants';
import DeleteConfirmation from '../../../Loads/components/DeleteConfirmation';

const LoadDetailsInfo = ({ load, treeData = null, cropzoneTotalArea = 0 }) => {
  const history = useHistory();
  const sourcesQnt = load?.sources?.length || 0;
  const [{ cropSeasons }] = useContext(Context);
  const [visible, setVisible] = useState(false);
  const sourceToDestination = `${load?.sourceType}_${load?.destinationType}`;
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const showDrawer = () => {
    setVisible(true);
  };
  const onCloseHandler = () => {
    setVisible(false);
  };

  const cropSeasonHandler = () => {
    const newCropSeason = cropSeasons?.find(
      cropSeason => cropSeason?.id === load?.cropSeasonId
    );
    return newCropSeason?.name;
  };

  const rightDetailsPanelHandler = () => {
    if (load?.sourceType === LOAD_TYPES.CROP_ZONE)
      return (
        <PropertyAsSourceDetails
          cropzoneTotalArea={cropzoneTotalArea}
          visible={visible}
          showDrawer={showDrawer}
          load={load}
        />
      );
    if (load?.sourceType === LOAD_TYPES.STORAGE)
      return <StorageAsSourceDetails load={load} />;
    return null;
  };

  const handleEdit = () => {
    history.push(`${paths.yieldV2}/loads/${load.id}/edit`);
  };

  if (!load) return null;

  return (
    <>
      <DeleteConfirmation
        id={load.id}
        open={showDeleteModal}
        onCancel={setShowDeleteModal}
        onResult={result => {
          history.push({
            pathname: `${paths.yieldV2}/loads`,
            toast: result
          });
        }}
      />
      <div className="w-full flex items-center justify-between p-4 bg-white border-b">
        <div className="flex items-center gap-6">
          <UiKitButton
            onClick={() => history.goBack()}
            icon={
              <FontAwesomeIcon
                data-testid="chevron-left-icon"
                icon={faChevronLeft}
                size="xs"
                style={{ marginRight: '4px' }}
              />
            }
            variant="outline"
          >
            Back
          </UiKitButton>
          <h1 className="text-2xl font-semibold flex flex-row">
            <img
              className="mr-3"
              src={ICONS_BY_LOAD_DIRECTION[sourceToDestination]}
              alt={sourceToDestination}
            />

            {load?.name}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          {' '}
          <UiKitButton
            icon={<img width={18} src={EditIcon} alt="edit icon" />}
            variant="outline"
            onClick={handleEdit}
          >
            Edit
          </UiKitButton>
          <UiKitButton
            icon={<img width={18} src={DeleteIcon} alt="delete icon" />}
            variant="outline"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete
          </UiKitButton>
        </div>
      </div>
      <div className="w-full p-4">
        <div className="flex gap-4">
          <section className="items-center bg-white rounded-lg shadow-sm-card">
            <div className="w-full border-b py-3 flex flex-row">
              <img className="ml-4" src={YieldIcon} alt="yield icon" />
              <div className="ml-2 font-normal">Yield</div>
            </div>
            <div className="m-4 mb-0">
              <div className="text-neutral-60">Yield quantity</div>
              <div className="flex flex-row">
                <div className="font-semibold text-3xl flex flex-row">
                  <FormattedNumber roundedTo={2}>{load?.load}</FormattedNumber>
                </div>
                <div className="text-sm font-normal	pt-4 ml-1">
                  {getLoadUnitAbbreviation(load?.loadUnit)}
                </div>
              </div>
            </div>
            {load?.sourceType !== LOAD_TYPES.STORAGE ? (
              <div className="m-4 mb-0">
                <div className="text-neutral-60">Avg. quantity</div>
                <div className="flex flex-row">
                  <div className="font-semibold text-3xl flex flex-row">
                    <FormattedNumber roundedTo={2}>
                      {Number.parseFloat(load?.load) /
                        load?.sources.reduce(
                          (accumulator, source) => accumulator + source.area,
                          0
                        )}
                    </FormattedNumber>
                  </div>
                  <div className="pt-4 ml-1">{`${getLoadUnitAbbreviation(
                    load.loadUnit
                  )} / ac`}</div>
                </div>
              </div>
            ) : null}
            {load?.loadUnit === 'bu' ? (
              <div className="mt-4 ml-4 mb-2 text-neutral-60">
                1.00 bushel = 56.00 lbs
              </div>
            ) : null}
          </section>

          {rightDetailsPanelHandler()}
        </div>
        <LoadDetailsDrawer
          cropzoneTotalArea={cropzoneTotalArea}
          sourcesQnt={sourcesQnt}
          cropSeasonHandler={cropSeasonHandler}
          visible={visible}
          onClose={onCloseHandler}
          load={load}
          treeData={treeData}
        />
        <LoadDetailsInfoTabs
          load={load}
          cropzoneTotalArea={cropzoneTotalArea}
        />
      </div>
    </>
  );
};

LoadDetailsInfo.propTypes = {
  load: TLoad.isRequired,
  cropzoneTotalArea: PropTypes.number,
  treeData: treeDataPropType
};

export default LoadDetailsInfo;
